import React, { useCallback, useContext, useEffect, useState } from 'react'
import { MerchantsProvider } from 'contexts'
import { useTranslation } from 'react-i18next'
import { Box, Box as Wrapper } from '@node-space/storybook-components/dist/Box'
import Loader from '@node-space/storybook-components/dist/Loader'
import { PageHeader } from '@node-space/storybook-components/dist/PageHeader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { Text } from '@node-space/storybook-components/dist/Text'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { toast } from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { ContactsSearchListLoader } from 'components/Contacts/ContactsSearchListLoader'
import EmptyContainer from 'components/EmptyContainer'
import { RequestError } from 'components/Errors/RequestError'
import FilterBar from 'components/FilterBar'
import { rootInitialState } from 'components/FilterBar/context/reducer'
import useFilterMerchant from 'components/FilterBar/hooks/useFilterMerchant'
import useFilterSearch from 'components/FilterBar/hooks/useFilterSearch'
import { useUpdateChannel } from 'hooks/mutations/useUpdateChannel'
import { useChannelsQuery, useMerchantsQuery, useSupportedCurrenciesQuery } from 'hooks/queries'
import { ChannelsHeaderButtons } from 'pages/Channels/components/ChannelsHeaderButtons'
import CreateChannelModal from 'pages/Channels/components/Modal/ChannelCreateModal'
import { getContactComplianceDetails } from 'pages/Contacts/utils/utils'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { Channel, UpdateChannel } from 'types/channels'
import { Contact } from 'types/contacts'
import { PartyDetailsType } from 'types/payments'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import ChannelsTable from './components/ChannelsTable'
import { Pagination } from './components/Pagination'
import { pageListOptions } from './constants/contants'

const ManageChannels = () => {
  const { t } = useTranslation()
  const { mutate, isPending: isPendingUpdateChannel } = useUpdateChannel()
  const [, setWalletAction] = useContext(WalletActionContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const { data: currencies, isPending: isCurrenciesLoading } = useSupportedCurrenciesQuery()
  const [page, setPage] = useState(0)
  const [nrOfRows, setNrOfRows] = useState(pageListOptions[0].value)
  const [offset, setOffset] = useState(0)
  const { merchant, setMerchant, resetMerchant } = useFilterMerchant()
  const { search, setSearch, resetSearch, resetIsSearchUsed } = useFilterSearch()
  const {
    data: { channels = [], paging } = {},
    isFetching: isChannelsFetching,
    isError: isChannelsError,
    refetch: refetchChannels,
  } = useChannelsQuery({
    max: Number(nrOfRows),
    offset,
    merchant,
    search,
  })

  const { data: { merchantList = [] } = {} } = useMerchantsQuery()

  const onMerchantSelected = useCallback(
    merchant => {
      setMerchant(merchant)
    },
    [merchant, search, Number(nrOfRows)]
  )

  useEffect(() => {
    refetchChannels()
  }, [search, offset])

  useEffect(() => {
    if (search == rootInitialState?.search && merchant == rootInitialState?.merchant) {
      refetchChannels()
    }
    setOffset(0)
  }, [search, merchant])

  useEffect(() => {
    return () => {
      resetSearch()
      resetMerchant()
    }
  }, [])

  const onResetAll = () => {
    setMerchant(rootInitialState?.merchant)
    setSearch(rootInitialState?.search)
    resetIsSearchUsed()
    resetMerchant()
  }

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_CHANNEL, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const isLoading = isChannelsFetching || isCurrenciesLoading
  const hasActiveFilters = merchant !== 'ALL' || search.length

  const handleContactSelect = (contact: Contact, channel: Channel) => {
    const data: UpdateChannel = {
      uuid: channel?.uuid,
      customerId: contact?.id,
      displayCurrency: channel?.displayCurrency,
      payCurrency: channel?.payCurrency,
      reference: channel?.reference,
      merchantId: channel?.merchantId,
      complianceDetails: getContactComplianceDetails(contact, PartyDetailsType.ORIGINATOR),
    }
    mutate(data, {
      onSuccess: () => {
        refetchChannels()
        setIsSheetOpen(false)
        toast({
          header: t('payments.channelPayments.contactAssigned', { contactName: contact?.name }),
          variant: 'success',
        })
      },
      onError: (error: BaseErrorResponse) => {
        logSentryErrorAndGetEventId(
          `Failed API request to /v2/channel with status ${error?.status} - updateChannel`,
          error,
          {
            data,
          }
        )
        toast({
          header: t('contacts.contactCouldNotBeAssigned'),
          body: t('contacts.contactAssignTechnicalError'),
          variant: 'error',
        })
      },
    })
  }

  const handleOpenChannelPaymentSheet = (channel: Channel) => {
    setWalletAction({
      actionId: ActionIds?.CONTACT_ASSIGN,
      header: t('payments.channelPayments.contactStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        handleContactSelect: (contact: Contact) => handleContactSelect(contact, channel),
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <Wrapper className="channels-container">
      <PageHeader
        description={t('manageChannelsIntroText')}
        {...{
          renderRightSection: (
            <ChannelsHeaderButtons
              refetchChannels={refetchChannels}
              setShowCreateModal={setShowCreateModal}
              showManageChannels={false}
            />
          ),
        }}
      />

      {!search && !channels?.length && merchant == 'ALL' ? (
        isLoading ? (
          <Box flex middle className="mt-10">
            <Loader />
          </Box>
        ) : (
          <StatusCard headline={t('noChannelsFound')}>
            <Text align="center" color="grey" size="sm">
              {t('noChannelsCreateChannel')}{' '}
              <TextAction linkTo={process.env.CHANNELS_LEARN_MORE} text={t('learnMore')} />.
            </Text>
          </StatusCard>
        )
      ) : (
        <>
          <FilterBar
            eventHandlers={{
              executor: refetchChannels,
              onMerchantSelected,
              showSearchResult: refetchChannels,
              onResetAll,
            }}
            conditions={{ isExportingCSV: false }}
            isChannelManagement={true}
          />
          {isLoading && (
            <Box flex centerChildren className="mt-10">
              <Loader />
            </Box>
          )}
          {isChannelsError && (
            <Box className="mt-10">
              <RequestError />
            </Box>
          )}

          {!isLoading && !channels?.length && hasActiveFilters && !isChannelsError && (
            <EmptyContainer>
              <Text size="base" weight="medium" color="black">
                {t('noChannelsFoundFiltered')}.
              </Text>

              <TextAction onClick={onResetAll} text={`${t('resetAllFilters')}?`} className="ml-2" />
            </EmptyContainer>
          )}
          {!isLoading && !!channels?.length && !isChannelsError && (
            <>
              <ChannelsTable
                channels={channels}
                currencies={currencies}
                handleOpenChannelPaymentSheet={handleOpenChannelPaymentSheet}
              />
              {paging?.total > Number(pageListOptions[0].value) && (
                <Pagination
                  page={page}
                  setPage={setPage}
                  setOffset={setOffset}
                  pagination={paging}
                  nrOfRows={nrOfRows}
                  setNrOfRows={setNrOfRows}
                />
              )}
            </>
          )}
        </>
      )}

      {showCreateModal && (
        <MerchantsProvider merchants={merchantList}>
          <CreateChannelModal closeCallback={() => setShowCreateModal(false)} />
        </MerchantsProvider>
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
      {isPendingUpdateChannel && <ContactsSearchListLoader />}
    </Wrapper>
  )
}

export default React.memo(ManageChannels)
